import React, { useState, useEffect, useRef } from 'react';
import { Input, Button, Spin, Switch, message, Select, Collapse } from 'antd';
import { RollbackOutlined, SendOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLanguage } from '../../../contexts/LanguageContext';
import { apiService } from '../services/apiService';
import { validateFlowWithSchema } from '../schema/validateFlowWithSchema';
import { validateWorkflowLogic, normalizeEdgeHandles, getOrCreateEdgeId } from '../utils';

const { TextArea } = Input;

const markdownChatStyles = { margin: 0, lineHeight: 1.5 };
const markdownComponents = (isUser) => ({
  p: ({ children }) => <p style={{ margin: '0 0 6px 0' }}>{children}</p>,
  strong: ({ children }) => <strong style={{ fontWeight: 700 }}>{children}</strong>,
  code: ({ inline, children }) =>
    inline ? (
      <code style={{ background: isUser ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.08)', padding: '1px 4px', borderRadius: 4, fontSize: '0.92em' }}>{children}</code>
    ) : (
      <code style={{ display: 'block', background: 'rgba(0,0,0,0.06)', padding: '6px 8px', borderRadius: 4, fontSize: 12, overflow: 'auto', marginTop: 4 }}>{children}</code>
    )
});

const AI_GENERATE_TIMEOUT_MS = 150000; // 150 秒逾時（AI 生成可能較慢）

const WorkflowDesignerAIChat = ({
  workflowDefinitionId,
  flowJson,
  onSuccess,
  isGenerating,
  setIsGenerating,
  nodeTypes,
  handleDeleteNode,
  onEdgeSwitch,
  setNodes,
  setEdges,
  t: tProp
}) => {
  const { t: tContext } = useLanguage();
  const t = typeof tProp === 'function' ? tProp : tContext;
  const messagesEndRef = useRef(null);
  const prevWorkflowIdRef = useRef(workflowDefinitionId);
  const messagesRef = useRef([]);
  const abortControllerRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  messagesRef.current = messages;
  const [aiPrompt, setAiPrompt] = useState('');
  const [includeCurrentJson, setIncludeCurrentJson] = useState(() => (flowJson || '').trim().length > 0);
  const [aiProviders, setAiProviders] = useState([]);
  const [selectedProviderKey, setSelectedProviderKey] = useState('');
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [sendDefaultSystemPrompt, setSendDefaultSystemPrompt] = useState(true);
  const [modifySystemPrompt, setModifySystemPrompt] = useState(false);
  const [customSystemPrompt, setCustomSystemPrompt] = useState('');

  useEffect(() => {
    loadAiProviders();
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    const hasExisting = (flowJson || '').trim().length > 0;
    setIncludeCurrentJson(hasExisting);
  }, [flowJson]);

  useEffect(() => {
    if (workflowDefinitionId) {
      const prevId = prevWorkflowIdRef.current;
      prevWorkflowIdRef.current = workflowDefinitionId;
      const currentMessages = messagesRef.current;
      const pending = currentMessages.filter((m) => !m.Id && (m.role || m.Role));
      if (pending.length > 0 && !prevId) {
        saveChatMessages(pending).then(() => loadChatHistory()).catch(() => setMessages((prev) => prev));
        return;
      }
      loadChatHistory();
    } else {
      prevWorkflowIdRef.current = null;
      setMessages([]);
    }
  }, [workflowDefinitionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadAiProviders = async () => {
    try {
      setLoadingProviders(true);
      const providers = await apiService.fetchAiProviders();
      setAiProviders(providers || []);
      setSelectedProviderKey((prev) => {
        if (prev) return prev;
        if (providers?.length > 0) {
          const active = providers.find((p) => p.active);
          return (active || providers[0]).providerKey;
        }
        return '';
      });
    } catch (error) {
      console.error('Failed to load AI providers', error);
      message.error(t?.('eformDesigner.loadAiProvidersFailed') || '無法載入 AI 提供者');
    } finally {
      setLoadingProviders(false);
    }
  };

  const loadChatHistory = async () => {
    if (!workflowDefinitionId) return;
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/WorkflowDefinitions/editing-ai-chat?workflowDefinitionId=${workflowDefinitionId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!res.ok) throw new Error('Failed to load chat history');
      const data = await res.json();
      setMessages(Array.isArray(data) ? data : []);
    } catch (e) {
      console.warn('Load workflow chat history failed', e);
      setMessages([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const saveChatMessages = async (items) => {
    const token = localStorage.getItem('token');
    const wfId = parseInt(workflowDefinitionId, 10);
    if (isNaN(wfId) || wfId <= 0) return;
    const body = items.map((m) => ({
      WorkflowDefinitionId: wfId,
      Role: m.role || m.Role,
      Content: m.content || m.Content || '',
      AttachmentInfo: m.attachmentInfo ?? m.AttachmentInfo ?? null,
      ResultWorkflowName: m.resultWorkflowName ?? m.ResultWorkflowName ?? null,
      JsonContent: m.jsonContent ?? m.JsonContent ?? null,
      MessageIndex: m.messageIndex ?? m.MessageIndex
    }));
    const res = await fetch('/api/WorkflowDefinitions/editing-ai-chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(body)
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || err.message || `HTTP ${res.status}`);
    }
    return res.json();
  };

  const enrichAndApplyFlow = (flow) => {
    if (!flow.nodes || !flow.edges || !nodeTypes || !setNodes || !setEdges || !handleDeleteNode || !onEdgeSwitch) {
      return;
    }
    const nodesWithIcons = flow.nodes.map((node) => {
      const nodeType = nodeTypes.find((n) => n.type === node.data?.type);
      let reactFlowType = 'default';
      if (node.data?.type === 'start') reactFlowType = 'input';
      else if (node.data?.type === 'end') reactFlowType = 'output';
      return {
        ...node,
        type: reactFlowType,
        data: {
          ...node.data,
          icon: nodeType ? nodeType.icon : null,
          iconColor: nodeType ? nodeType.iconColor : null,
          label: node.data?.label || nodeType?.label || node.data?.type,
          taskName: node.data?.taskName || nodeType?.label || node.data?.type,
          onDelete: node.data?.type === 'start' ? null : handleDeleteNode
        }
      };
    });
    const edgesWithStyle = flow.edges.map((edge, idx) => {
      const handles = normalizeEdgeHandles(nodesWithIcons, edge);
      const { sourceHandle, targetHandle } = handles;
      return {
        ...edge,
        id: getOrCreateEdgeId(edge, handles, idx),
        sourceHandle,
        targetHandle,
        type: edge.type || 'smoothstep',
        animated: edge.animated !== undefined ? edge.animated : true,
        style: edge.style || { strokeWidth: 3, stroke: '#1890ff' },
        markerEnd: edge.markerEnd || { type: 'arrowclosed', width: 12, height: 12, color: '#1890ff' },
        data: { ...(edge.data || {}), onEdgeSwitch }
      };
    });
    setNodes(nodesWithIcons);
    setEdges(edgesWithStyle);
  };

  const handleSend = async () => {
    if (!aiPrompt.trim()) {
      message.warning(t?.('workflowDesigner.aiChat.pleaseEnterPrompt') || '請輸入流程描述');
      return;
    }
    if (aiProviders.length > 0 && !selectedProviderKey) {
      message.warning(t?.('eformDesigner.aiProviderRequired') || '請選擇 AI 提供者');
      return;
    }

    setIsGenerating?.(true);
    const nextIndex = messages.length;

    // 取消上一次尚未完成的請求（若有）
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const timeoutId = setTimeout(() => controller.abort(), AI_GENERATE_TIMEOUT_MS);

    try {
      const token = localStorage.getItem('token');
      const requestData = {
        prompt: aiPrompt.trim(),
        includeCurrentJson,
        providerKey: selectedProviderKey || null
      };
      if (includeCurrentJson && (flowJson || '').trim()) requestData.currentJson = (flowJson || '').trim();

      const response = await fetch('/api/WorkflowDefinitions/ai-generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(requestData),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        throw new Error(`HTTP ${response.status}${errText ? ': ' + errText.slice(0, 200) : ''}`);
      }

      const result = await response.json();

      if (!result.success) {
        message.error((t?.('workflowDesigner.aiChat.generationFailed') || '生成失敗') + (result.error || ''));
        return;
      }

      const flow = { nodes: result.nodes || [], edges: result.edges || [] };
      const schemaResult = validateFlowWithSchema(flow);
      if (!schemaResult.valid) {
        message.error((t?.('workflowDesigner.aiChat.schemaValidationFailed') || '流程格式驗證失敗：') + schemaResult.errors.map((e) => e.message).join('; '));
        return;
      }

      const { errors } = validateWorkflowLogic(flow.nodes, flow.edges, t || (() => ''));
      if (errors && errors.length > 0) {
        message.error((t?.('workflowDesigner.aiChat.logicValidationFailed') || '流程邏輯驗證失敗：') + errors.join('; '));
        return;
      }

      message.success(t?.('workflowDesigner.aiChat.applied') || '已套用流程');
      enrichAndApplyFlow(flow);

      const summary = result.summary && result.summary.trim() ? result.summary.trim() : (t?.('workflowDesigner.aiChat.updated') || '已根據您的要求更新流程');
      const userItem = {
        WorkflowDefinitionId: workflowDefinitionId || 0,
        Role: 'user',
        Content: aiPrompt.trim(),
        AttachmentInfo: null,
        ResultWorkflowName: null,
        JsonContent: null,
        MessageIndex: nextIndex
      };
      const assistantItem = {
        WorkflowDefinitionId: workflowDefinitionId || 0,
        Role: 'assistant',
        Content: summary,
        AttachmentInfo: null,
        ResultWorkflowName: null,
        JsonContent: JSON.stringify(flow),
        MessageIndex: nextIndex + 1
      };

      if (workflowDefinitionId) {
        try {
          await saveChatMessages([userItem, assistantItem]);
        } catch (e) {
          console.warn('Save workflow chat failed', e);
          message.warning(t?.('workflowDesigner.aiChat.chatSaveFailed') || '未能保存對話記錄：' + (e?.message || ''));
        }
      }

      setMessages((prev) => [
        ...prev,
        { ...userItem, Id: null, CreatedAt: new Date().toISOString() },
        { ...assistantItem, Id: null, JsonContent: JSON.stringify(flow), CreatedAt: new Date().toISOString() }
      ]);
      setAiPrompt('');
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        message.error(t?.('workflowDesigner.aiChat.timeout') || 'AI 生成逾時，請重試或縮短流程描述');
      } else {
        console.error('Workflow AI generate error', error);
        message.error((t?.('workflowDesigner.aiChat.generationFailed') || '生成失敗：') + (error.message || ''));
      }
    } finally {
      setIsGenerating?.(false);
    }
  };

  const handleUndoToVersion = (msg) => {
    const raw = msg.jsonContent || msg.JsonContent;
    if (!raw || typeof onSuccess !== 'function') return;
    try {
      const flow = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (setNodes && setEdges && nodeTypes && handleDeleteNode && onEdgeSwitch) {
        enrichAndApplyFlow(flow);
      } else {
        onSuccess(flow);
      }
      message.success(t?.('workflowDesigner.aiChat.restored') || '已還原到此版本');
    } catch (e) {
      message.error(t?.('workflowDesigner.aiChat.restoreFailed') || '還原失敗');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      <div
        style={{
          flex: '1 1 0%',
          minHeight: 80,
          overflowY: 'auto',
          overflowX: 'hidden',
          border: '1px solid #e8e8e8',
          borderRadius: 8,
          padding: 8,
          marginBottom: 8,
          backgroundColor: '#fafafa'
        }}
      >
        {loadingHistory && (
          <div style={{ textAlign: 'center', padding: 16 }}>
            <Spin size="small" />
          </div>
        )}
        {!loadingHistory && messages.length === 0 && (
          <div style={{ textAlign: 'center', color: '#999', fontSize: 12, padding: 16 }}>
            {t?.('workflowDesigner.aiChat.emptyHint') || '輸入流程描述，AI 將協助生成或修改流程'}
          </div>
        )}
        {!loadingHistory &&
          messages.map((msg) => {
            const role = msg.role || msg.Role;
            const content = msg.content || msg.Content;
            const hasJson = !!(msg.jsonContent || msg.JsonContent);
            return (
              <div
                key={msg.Id || `${msg.MessageIndex}-${role}`}
                style={{
                  display: 'flex',
                  justifyContent: role === 'user' ? 'flex-end' : 'flex-start',
                  marginBottom: 8
                }}
              >
                <div
                  style={{
                    maxWidth: '92%',
                    padding: '8px 12px',
                    borderRadius: 12,
                    backgroundColor: role === 'user' ? '#1890ff' : '#f0f0f0',
                    color: role === 'user' ? '#fff' : '#333',
                    fontSize: 13
                  }}
                >
                  {role === 'assistant' ? (
                    <div style={markdownChatStyles}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents(false)}>
                        {content || ''}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{content}</div>
                  )}
                  {role === 'assistant' && hasJson && (
                    <Button
                      type="link"
                      size="small"
                      icon={<RollbackOutlined />}
                      onClick={() => handleUndoToVersion(msg)}
                      style={{ padding: 0, marginTop: 6, color: '#1890ff' }}
                    >
                      {t?.('workflowDesigner.aiChat.restoreToThisVersion') || '還原到此版本'}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        <div ref={messagesEndRef} />
      </div>

      {!workflowDefinitionId && (
        <div style={{ fontSize: 11, color: '#999', marginBottom: 6 }}>
          {t?.('workflowDesigner.aiChat.saveHint') || '儲存流程後，對話紀錄將寫入'}
        </div>
      )}

      <div style={{ flexShrink: 0 }}>
        <div style={{ position: 'relative', marginBottom: 8 }}>
          <TextArea
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            placeholder={t?.('workflowDesigner.aiChat.placeholder') || '例如：開始後發送 WhatsApp 再等回覆，然後結束'}
            rows={4}
            style={{ paddingRight: 40, fontSize: 13 }}
            disabled={!!isGenerating}
          />
          <Button
            type="primary"
            size="small"
            icon={<SendOutlined />}
            onClick={handleSend}
            loading={!!isGenerating}
            disabled={!aiPrompt.trim() || (aiProviders.length > 0 && !selectedProviderKey)}
            style={{ position: 'absolute', right: 6, bottom: 6, minWidth: 32, height: 28, padding: '0 8px' }}
          />
        </div>
        {(flowJson || '').trim() && (
          <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <Switch size="small" checked={includeCurrentJson} onChange={setIncludeCurrentJson} disabled={!!isGenerating} style={{ marginRight: 4 }} />
            {t?.('workflowDesigner.aiChat.includeCurrentFlow') || '包含當前流程'}
          </label>
        )}

        <Collapse
          size="small"
          items={[
            {
              key: '1',
              label: t?.('eformDesigner.advancedOptions') || '進階選項',
              children: (
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontSize: 12 }}>{t?.('eformDesigner.aiProvider') || 'AI 提供者'}</label>
                  <Select
                    value={selectedProviderKey || undefined}
                    onChange={setSelectedProviderKey}
                    placeholder={t?.('eformDesigner.aiProviderPlaceholder')}
                    style={{ width: '100%' }}
                    size="small"
                    loading={loadingProviders}
                    allowClear
                    options={aiProviders.map((p) => ({
                      value: p.providerKey,
                      label: `${p.displayName}${p.active ? '' : ' (未啟用)'}`,
                      disabled: p.active === false
                    }))}
                  />
                </div>
              )
            }
          ]}
        />
      </div>
    </div>
  );
};

export default WorkflowDesignerAIChat;
